/* ============================================
   CART DRAWER — Birdie Shots
   Depends on: cart.js (window.Cart)
   ============================================ */

'use strict';

(function () {
  const drawer  = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');
  const openBtn = document.getElementById('cart-toggle');
  const closeBtn = document.getElementById('cart-drawer-close');

  if (!drawer) return;

  // ---- Open / Close ----

  function open() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    openBtn?.setAttribute('aria-expanded', 'true');
    closeBtn?.focus();
    refreshCart();
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('is-visible');
    document.body.style.overflow = '';
    openBtn?.setAttribute('aria-expanded', 'false');
    openBtn?.focus();
  }

  window.openCartDrawer  = open;
  window.closeCartDrawer = close;

  openBtn?.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  // ---- Quantity / Remove — delegated ONCE on the drawer, never re-attached ----

  drawer.addEventListener('click', async function (e) {
    const qtyBtn    = e.target.closest('.cart-item__qty-btn');
    const removeBtn = e.target.closest('.cart-item__remove');
    const giftToggle = e.target.closest('#gift-toggle');
    const saveGift  = e.target.closest('#save-gift-btn');

    if (qtyBtn) {
      const key    = qtyBtn.dataset.key;
      const action = qtyBtn.dataset.action;
      const input  = drawer.querySelector(`.cart-item__qty-input[data-key="${key}"]`);
      if (!input) return;
      const qty = action === 'increase'
        ? parseInt(input.value) + 1
        : Math.max(0, parseInt(input.value) - 1);
      await updateItem(key, qty);
    }

    if (removeBtn) {
      await updateItem(removeBtn.dataset.key, 0);
    }

    if (giftToggle) {
      const fields  = drawer.querySelector('#gift-fields');
      const isOpen  = fields?.classList.toggle('is-open');
      giftToggle.setAttribute('aria-expanded', String(isOpen));
      const arrow = giftToggle.querySelector('svg');
      if (arrow) arrow.style.transform = isOpen ? 'rotate(180deg)' : '';
    }

    if (saveGift) {
      const message  = drawer.querySelector('#gift-message')?.value || '';
      const giftWrap = drawer.querySelector('#gift-wrap-check')?.checked || false;
      try {
        await window.fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { gift_message: message, gift_wrap: giftWrap ? 'Yes' : 'No' },
          }),
        });
        window.showToast?.('Gift message saved!', 'success');
      } catch {
        window.showToast?.('Could not save message.', 'error');
      }
    }
  });

  drawer.addEventListener('change', async function (e) {
    const input = e.target.closest('.cart-item__qty-input');
    if (input) {
      const qty = Math.max(0, parseInt(input.value) || 0);
      await updateItem(input.dataset.key, qty);
    }
  });

  // ---- Cart update ----

  async function updateItem(key, quantity) {
    try {
      const res = await window.fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error();
      await refreshCart();
    } catch {
      window.showToast?.('Could not update cart.', 'error');
    }
  }

  // ---- Render ----

  async function refreshCart() {
    try {
      const cart = await window.Cart.fetch();
      window.Cart.updateUI(cart);

      const body = document.getElementById('cart-drawer-body');
      if (!body) return;

      const footer = drawer.querySelector('.cart-drawer__footer');

      if (cart.item_count === 0) {
        body.innerHTML = `
          <div class="cart-drawer__empty">
            <div class="cart-drawer__empty-icon">⛳</div>
            <h3>Your cart is empty</h3>
            <p>Looks like you haven't added anything yet.</p>
            <a href="/collections/all" class="btn btn--primary" onclick="window.closeCartDrawer()">Shop Now</a>
          </div>`;
        if (footer) footer.style.display = 'none';
        return;
      }

      if (footer) footer.style.display = '';

      const itemsHtml = cart.items.map((item) => `
        <div class="cart-item" data-key="${item.key}">
          <img class="cart-item__image" src="${item.image}" alt="${item.title}" width="80" height="80" loading="lazy">
          <div>
            <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title'
              ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
            <div class="cart-item__meta">
              <span class="cart-item__price">${window.Cart.formatMoney(item.final_line_price, cart.currency)}</span>
              <div class="cart-item__quantity">
                <button class="cart-item__qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                <input type="number" class="cart-item__qty-input" value="${item.quantity}" min="1" data-key="${item.key}" aria-label="Quantity">
                <button class="cart-item__qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
              </div>
            </div>
            <button class="cart-item__remove" data-key="${item.key}">Remove</button>
          </div>
        </div>`).join('');

      // Gift section is static HTML — event delegation on the drawer handles clicks
      body.innerHTML = `
        <div id="cart-items">${itemsHtml}</div>
        <div class="cart-drawer__gift">
          <button class="cart-drawer__gift-toggle" id="gift-toggle" aria-expanded="false">
            🎁 <span>Add a Gift Message</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-left:auto;">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          <div class="cart-drawer__gift-fields" id="gift-fields">
            <label class="form-label" style="display:flex;align-items:center;gap:0.5rem;cursor:pointer;">
              <input type="checkbox" id="gift-wrap-check"> Add Gift Wrapping (+$5)
            </label>
            <div class="form-field">
              <label class="form-label" for="gift-message">Gift Message</label>
              <textarea id="gift-message" class="form-textarea" rows="3" placeholder="Write your personal message here..."></textarea>
            </div>
            <button type="button" class="btn btn--outline-dark btn--small" id="save-gift-btn">Save Message</button>
          </div>
        </div>`;
    } catch (err) {
      console.error('Cart refresh failed:', err);
    }
  }
})();
